"use client";

import { useState, useRef, useEffect } from "react";
import { Check, MessageCircle } from "lucide-react";
import { SiWhatsapp, SiInstagram, SiWechat } from "@icons-pack/react-simple-icons";
import { config } from "@/lib/config";
import { SOCIAL_COLORS } from "@/lib/social-colors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useIsMobile";

interface ProductEnquireOptionsProps {
  productTitle: string;
  productId: string;
  /** Descriptive slug — used in the prefilled URL when present (falls back to id). */
  productSlug?: string | null;
  /** Icon-only trigger (e.g. for product cards) or full "Enquire" button (e.g. modal footer) */
  variant?: "icon" | "button";
  /** Pass to match Add to cart styling */
  className?: string;
  /** Chosen variant label (e.g. "Deluxe") — woven into the prefilled message. */
  variantLabel?: string;
}

function buildWhatsAppMessage(
  productTitle: string,
  idOrSlug: string,
  variantLabel?: string
): string {
  const titleWithVariant = variantLabel
    ? `${productTitle} (${variantLabel})`
    : productTitle;
  return `Hi, I'd like to enquire about ${titleWithVariant} — availability / custom arrangement. ${config.siteUrl}/p/${encodeURIComponent(idOrSlug)}`;
}

function EnquireOptionsList({
  productTitle,
  productId,
  productSlug,
  variantLabel,
  onAction,
  copiedWechat,
  onCopyWechat,
}: {
  productTitle: string;
  productId: string;
  productSlug?: string | null;
  variantLabel?: string;
  onAction?: () => void;
  copiedWechat: boolean;
  onCopyWechat: () => void;
}) {
  const message = buildWhatsAppMessage(productTitle, productSlug || productId, variantLabel);

  const itemClass =
    "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors focus:outline-hidden";

  const iconColor = "var(--muted-foreground)";
  return (
    <div className="flex flex-col gap-2">
      <a
        href={`${config.whatsapp}?text=${encodeURIComponent(message)}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`group ${itemClass} border-border bg-background hover:bg-muted focus:bg-muted`}
        style={{ ["--icon-hover" as string]: SOCIAL_COLORS.whatsapp.icon }}
        onClick={onAction}
      >
        {/* aria-hidden: the icon is decorative next to the visible label, and
            simple-icons renders a <title> ("WhatsApp") that would otherwise be
            announced too — "WhatsApp WhatsApp (opens in new tab)". */}
        <SiWhatsapp aria-hidden className="h-5 w-5 shrink-0 transition-colors group-hover:text-(--icon-hover)" color={iconColor} />
        WhatsApp
        {/* No aria-label here on purpose — the visible text is the accessible
            name, so the sr-only suffix is appended to it rather than replaced. */}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
      <a
        href={config.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className={`group ${itemClass} border-border bg-background hover:bg-muted focus:bg-muted`}
        style={{ ["--icon-hover" as string]: iconColor }}
        onClick={onAction}
      >
        <SiInstagram aria-hidden className="h-5 w-5 shrink-0 transition-colors group-hover:text-(--icon-hover)" color={iconColor} />
        Instagram
        <span className="sr-only"> (opens in new tab)</span>
      </a>
      <button
        type="button"
        className={`group ${itemClass} border-border bg-background hover:bg-muted focus:bg-muted`}
        style={{ ["--icon-hover" as string]: SOCIAL_COLORS.wechat.icon }}
        onClick={() => {
          onCopyWechat();
          onAction?.();
        }}
      >
        {copiedWechat ? (
          <Check className="h-5 w-5 shrink-0 text-green-600" />
        ) : (
          <SiWechat className="h-5 w-5 shrink-0 transition-colors group-hover:text-(--icon-hover)" color={iconColor} />
        )}
        WeChat
      </button>
    </div>
  );
}

export function ProductEnquireOptions({
  productTitle,
  productId,
  productSlug,
  variant = "button",
  className,
  variantLabel,
}: ProductEnquireOptionsProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [copiedWechat, setCopiedWechat] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopyWechat = async () => {
    try {
      await navigator.clipboard.writeText(config.wechatId);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = config.wechatId;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedWechat(true);
    toast.success("WeChat ID copied");
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopiedWechat(false), 2000);
  };

  const close = () => setOpen(false);

  // ONE prop list, rendered by BOTH breakpoints. It used to be written out
  // twice — once inside the mobile Sheet, once inside the desktop Dialog — and
  // the desktop copy omitted `variantLabel`, so a desktop enquiry reached the
  // florist without the variant the customer had picked. Silent data loss on a
  // real sales lead. Keep this hoisted: a second literal is how the drift
  // returns.
  const optionsList = (
    <EnquireOptionsList
      productTitle={productTitle}
      productId={productId}
      productSlug={productSlug}
      variantLabel={variantLabel}
      onAction={close}
      copiedWechat={copiedWechat}
      onCopyWechat={handleCopyWechat}
    />
  );

  const trigger =
    variant === "icon" ? (
      <Button
        size="icon"
        className={className}
        aria-label="Enquire for availability"
      >
        <MessageCircle className="h-4 w-4" />
      </Button>
    ) : (
      <Button
        className={className}
        aria-label="Enquire for availability"
      >
        Enquire
      </Button>
    );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {trigger}
        </SheetTrigger>
        <SheetContent side="bottom" className="flex max-h-[85vh] flex-col overflow-hidden rounded-t-2xl pb-8">
          <SheetHeader className="shrink-0">
            <SheetTitle>Enquire via</SheetTitle>
            <SheetDescription className="sr-only">Choose a channel to enquire about this product</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-1">
            {optionsList}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Enquire via</DialogTitle>
          <DialogDescription className="sr-only">Choose a channel to enquire about this product</DialogDescription>
        </DialogHeader>
        <div className="pt-1">
          {optionsList}
        </div>
      </DialogContent>
    </Dialog>
  );
}
